/**
 * Locale detection utility based on browser timezone
 */

import countryCodes from './countryCodes.json';

// Service countries we currently support
export const SERVICE_COUNTRIES = {
  'NZ': { name: 'New Zealand', currency: 'NZD', dialCode: '+64' },
  'AU': { name: 'Australia', currency: 'AUD', dialCode: '+61' },
  'US': { name: 'United States', currency: 'USD', dialCode: '+1' },
  'IN': { name: 'India', currency: 'INR', dialCode: '+91' },
  'GB': { name: 'United Kingdom', currency: 'GBP', dialCode: '+44' },
};

// Map timezones to country codes
const TIMEZONE_TO_COUNTRY = {
  // India
  'Asia/Kolkata': 'IN',
  'Asia/Calcutta': 'IN',
  
  // United States
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'America/Phoenix': 'US',
  'America/Anchorage': 'US',
  'Pacific/Honolulu': 'US',
  'America/Detroit': 'US',
  'America/Indianapolis': 'US',
  'America/Kentucky/Louisville': 'US',
  'America/Kentucky/Monticello': 'US',
  'America/North_Dakota/Beulah': 'US',
  'America/North_Dakota/Center': 'US',
  'America/North_Dakota/New_Salem': 'US',
  
  // United Kingdom
  'Europe/London': 'GB',
  
  // Australia
  'Australia/Sydney': 'AU',
  'Australia/Melbourne': 'AU',
  'Australia/Brisbane': 'AU',
  'Australia/Perth': 'AU',
  'Australia/Adelaide': 'AU',
  'Australia/Hobart': 'AU',
  'Australia/Darwin': 'AU',
  'Australia/Canberra': 'AU',
  
  // New Zealand
  'Pacific/Auckland': 'NZ',
  'Pacific/Chatham': 'NZ',
};

// Default fallback
const DEFAULT_COUNTRY = 'US';
const DEFAULT_CURRENCY = 'USD';
const DEFAULT_DIAL_CODE = '+1';

/**
 * Detect user's country code, currency, and dial code from browser timezone
 * @returns {Object} { countryCode, currency, dialCode, isServiceCountry, countryName, timezone, detectionMethod }
 */
export const detectUserLocale = () => {
  try {
    // Get browser timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Try to get country from timezone first
    let countryCode = TIMEZONE_TO_COUNTRY[timezone];
    let detectionMethod = 'timezone';
    
    if (!countryCode) {
      // Fallback to locale if timezone not in our map
      const locale = navigator.language || navigator.languages?.[0] || 'en-US';
      const parts = locale.split('-');
      countryCode = parts.length > 1 ? parts[1].toUpperCase() : null;
      detectionMethod = 'locale';
      
      if (!countryCode) {
        // Final fallback to language
        const langMap = {
          'en': 'US',
          'hi': 'IN',
          'es': 'US',
          'fr': 'FR',
          'de': 'DE',
          'ja': 'JP',
          'zh': 'CN',
        };
        const lang = parts[0].toLowerCase();
        countryCode = langMap[lang] || DEFAULT_COUNTRY;
        detectionMethod = 'language';
      }
    }
    
    // Check if it's a service country
    const isServiceCountry = !!SERVICE_COUNTRIES[countryCode];
    
    // Get country details
    let currency, dialCode, countryName;
    
    if (isServiceCountry) {
      const serviceCountry = SERVICE_COUNTRIES[countryCode];
      currency = serviceCountry.currency;
      dialCode = serviceCountry.dialCode;
      countryName = serviceCountry.name;
    } else {
      // Not a service country - use defaults
      currency = DEFAULT_CURRENCY;
      
      // Try to find country name and dial code from countryCodes.json
      const countryData = countryCodes.find(c => c.code === countryCode);
      countryName = countryData?.name || 'Unknown';
      dialCode = countryData?.dial_code || DEFAULT_DIAL_CODE;
    }
    
    return {
      countryCode,
      currency,
      dialCode,
      isServiceCountry,
      countryName,
      timezone,
      detectionMethod,
    };
  } catch (error) {
    console.error('Locale detection failed:', error);
    return {
      countryCode: DEFAULT_COUNTRY,
      currency: DEFAULT_CURRENCY,
      dialCode: DEFAULT_DIAL_CODE,
      isServiceCountry: true,
      countryName: 'United States',
      timezone: 'America/New_York',
      detectionMethod: 'fallback',
    };
  }
};

/**
 * Get filtered country codes for service countries only
 * @returns {Array} Array of country objects for service countries
 */
export const getServiceCountryCodes = () => {
  return countryCodes.filter(country => 
    SERVICE_COUNTRIES[country.code]
  );
};

/**
 * Check if a country code is in service countries
 * @param {string} countryCode - ISO country code
 * @returns {boolean}
 */
export const isServiceCountry = (countryCode) => {
  return !!SERVICE_COUNTRIES[countryCode?.toUpperCase()];
};

/**
 * Get currency for a country code
 * @param {string} countryCode - ISO country code
 * @returns {string} Currency code
 */
export const getCurrencyForCountry = (countryCode) => {
  const country = SERVICE_COUNTRIES[countryCode?.toUpperCase()];
  return country?.currency || DEFAULT_CURRENCY;
};

/**
 * Get dial code for a country code
 * @param {string} countryCode - ISO country code
 * @returns {string} Dial code
 */
export const getDialCodeForCountry = (countryCode) => {
  const country = SERVICE_COUNTRIES[countryCode?.toUpperCase()];
  if (country) return country.dialCode;
  
  // Fallback to countryCodes.json
  const countryData = countryCodes.find(c => c.code === countryCode?.toUpperCase());
  return countryData?.dial_code || DEFAULT_DIAL_CODE;
};

/**
 * Resolve dial code from /users/me profile fields.
 * Priority: address.country → currency/region → phone prefix → locale fallback
 */
export const getDialCodeFromUserProfile = (userData) => {
  if (!userData) {
    return detectUserLocale().dialCode;
  }

  const addressCountry = userData?.address?.country;
  if (addressCountry && !/^NA/i.test(String(addressCountry).trim())) {
    return getDialCodeForCountry(addressCountry);
  }

  const currencyOrRegion = userData?.currency || userData?.region;
  if (currencyOrRegion) {
    const upper = String(currencyOrRegion).toUpperCase();
    const byCurrency = Object.values(SERVICE_COUNTRIES).find(c => c.currency === upper);
    if (byCurrency) return byCurrency.dialCode;
    if (upper.length === 2) return getDialCodeForCountry(upper);
  }

  const phone = userData?.phone
    || userData?.identities?.find(id => id.provider === 'sms')?.identifier;
  if (phone?.startsWith('+')) {
    const dialCodes = Object.values(SERVICE_COUNTRIES)
      .map(c => c.dialCode)
      .sort((a, b) => b.length - a.length);
    const matched = dialCodes.find(dial => phone.startsWith(dial));
    if (matched) return matched;
  }

  return detectUserLocale().dialCode;
};
